// The day-level break top-up of a multi-entry day, mirrored for the live notice.
//
// Server twin: ConfigurableTimeSheets::MultiEntryBreakTopUp, whose placement is
// Timesheet::BreakPlacement. The mobile app carries the same port. Change the
// three together, or the form announces a pause the save puts somewhere else.
//
// Everything is in minutes since midnight. A block is
//   { key, from, to, periods: [[from, to], ...], ownBreak, mutable }
// where +periods+ are the von/bis rows the author entered, +ownBreak+ the
// minutes of pause the block already carries (the periods' sum, or a scalar
// Pause), and +mutable+ false for a saved block the save only counts, never
// writes (approved, or recorded) — it defaults to true.

const QUARTER_HOUR = 15

// Where a shortfall goes (decision D3 of docs/plans/check-in-break-top-up.md):
// directly after the day's longest pause, as far as the stretch behind it allows,
// then into the longest worked stretch from the quarter hour at or before its
// midpoint. Ties go to the earlier stretch. Returns [[key, from, to], ...].
export function placeBreak(windows, gaps, shortfall) {
  const placed = []
  let remaining = shortfall

  const placedIn = (window) =>
    placed.filter(([key]) => key === window.key).map(([, from, to]) => [from, to])

  const freeStretches = (window) => {
    const taken = [...window.periods, ...placedIn(window)].sort(
      (a, b) => a[0] - b[0] || a[1] - b[1],
    )
    let cursor = window.from
    const stretches = []
    taken.forEach(([from, to]) => {
      if (from > cursor) stretches.push([cursor, from])
      cursor = Math.max(cursor, to)
    })
    if (window.to > cursor) stretches.push([cursor, window.to])
    return stretches
  }

  const place = (window, from, limit) => {
    const to = Math.min(limit, from + remaining)
    if (to <= from) return
    placed.push([window.key, from, to])
    remaining -= to - from
  }

  const longestStretch = () => {
    let best = null
    windows.forEach((window) => {
      freeStretches(window).forEach(([from, to]) => {
        if (to <= from) return
        if (
          !best ||
          to - from > best[2] - best[1] ||
          (to - from === best[2] - best[1] && from < best[1])
        ) {
          best = [window, from, to]
        }
      })
    })
    return best
  }

  // 1. Extend the longest pause (a gap between two blocks included).
  const pauses = [
    ...windows.flatMap((window) => window.periods.map((period) => [window, period])),
    ...gaps.map(([window, from, to]) => [window, [from, to]]),
  ]
  let longest = null
  pauses.forEach(([window, [from, to]]) => {
    if (!longest || to - from > longest[2] || (to - from === longest[2] && from < longest[1])) {
      longest = [window, from, to - from, to]
    }
  })
  if (longest) {
    const [window, , , pauseEnd] = longest
    const stretch = freeStretches(window).find(([from]) => from === pauseEnd)
    if (stretch) place(window, pauseEnd, stretch[1])
  }

  // 2. Whatever is left goes into the longest worked stretch.
  let stretch = longestStretch()
  while (remaining > 0 && stretch) {
    const [window, from, to] = stretch
    const length = Math.min(remaining, to - from)
    const midpoint = from + Math.floor((to - from) / 2)
    const start = Math.min(
      Math.max(Math.floor(midpoint / QUARTER_HOUR) * QUARTER_HOUR, from),
      to - length,
    )
    place(window, start, start + length)
    stretch = longestStretch()
  }

  return placed
}

// Required total break for +netMinutes+: the largest break_minutes among the
// thresholds the day crosses. Mirrors Timesheet::BreakThresholds.
export function requiredBreakMinutes(netMinutes, thresholds, atLeast) {
  const netHours = netMinutes / 60
  return (thresholds || []).reduce((required, threshold) => {
    const after = Number(threshold.after_hours)
    const crossed = atLeast ? after <= netHours : after < netHours
    return crossed ? Math.max(required, Number(threshold.break_minutes) || 0) : required
  }, 0)
}

// What the save will add to a day of +blocks+, or null when the day has fewer
// than two complete blocks (the per-entry rule judges a single one).
//
//   gapBreakMax  undefined when gaps are not documented as pauses; otherwise the
//                template's "Maximale Lückendauer" (0 = no limit)
//   periodsMode  whether placed minutes are von/bis rows (true) or added to a
//                scalar Pause, whose placement only names the block
export function dayTopUp({
  blocks,
  thresholds,
  atLeast = false,
  gapBreakMax,
  periodsMode = false,
}) {
  const complete = blocks.filter((block) => block.to > block.from)
  if (complete.length < 2 || complete.length !== blocks.length) return null

  const ordered = [...complete].sort((a, b) => a.from - b.from)
  const net = ordered.reduce((sum, block) => sum + (block.to - block.from) - block.ownBreak, 0)
  const required = requiredBreakMinutes(net, thresholds, atLeast)

  const mutable = (block) => block.mutable !== false
  // A gap counts as taken either way; the placement may only extend one into a
  // block it can write (MultiEntryBreakTopUp#gaps).
  const gaps = []
  if (gapBreakMax !== undefined) {
    for (let index = 1; index < ordered.length; index += 1) {
      const gap = ordered[index].from - ordered[index - 1].to
      if (gap > 0 && !(gapBreakMax > 0 && gap > gapBreakMax)) {
        gaps.push([ordered[index], ordered[index - 1].to, ordered[index].from])
      }
    }
  }

  const taken =
    ordered.reduce((sum, block) => sum + block.ownBreak, 0) +
    gaps.reduce((sum, [, from, to]) => sum + (to - from), 0)
  const shortfall = Math.max(required - taken, 0)
  if (shortfall === 0) return { required, taken, shortfall, placements: [] }

  // A scalar Pause says how long, not when: keep it out of the placeable stretch.
  const windows = ordered.filter(mutable).map((block) => ({
    key: block.key,
    from: block.from,
    to: periodsMode ? block.to : Math.max(block.to - block.ownBreak, block.from),
    periods: periodsMode ? block.periods : [],
  }))
  const windowGaps = gaps
    .filter(([block]) => mutable(block))
    .map(([block, from, to]) => [windows.find((w) => w.key === block.key), from, to])
  const placements = placeBreak(windows, windowGaps, shortfall)

  return { required, taken, shortfall, placements }
}

// A Pause-Block between two chained blocks is stored on the block it follows:
// that block's window runs over the pause, and the pause is a break on it — a
// von/bis row in periods mode, otherwise minutes on its scalar Pause.
//
// +breaks+ are { afterKey, from, to }. Returns new blocks; the input is untouched.
export function foldChainBreaks(blocks, breaks, periodsMode) {
  return blocks.map((block) => {
    const own = breaks.filter((pause) => pause.afterKey === block.key && pause.to > pause.from)
    if (own.length === 0) return block

    const to = Math.max(block.to, ...own.map((pause) => pause.to))
    const added = own.reduce((sum, pause) => sum + (pause.to - pause.from), 0)
    return {
      ...block,
      to,
      periods: periodsMode
        ? [...block.periods, ...own.map((pause) => [pause.from, pause.to])]
        : block.periods,
      ownBreak: block.ownBreak + added,
    }
  })
}
